import type { JSONB } from '@risksmart-app/domain/src/types/common.types';
import type { QuestionnaireTemplateVersionStatus as Questionnaire_Template_Version_Status_Enum } from '@risksmart-app/domain/src/types/consts/questionnaire-template-version-status';
import type { UpdateQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateQuestionnaireTemplateVersionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateQuestionnaireTemplateVersionInput {
  Id: string;
  Version: string;
  Status: Questionnaire_Template_Version_Status_Enum;
  Schema: JSONB;
  UISchema: JSONB;
  CustomAttributeData?: Record<string, unknown> | null;
}

interface UpdateQuestionnaireTemplateVersionOptions {
  /**
   * Threads Apollo request headers on the legacy GraphQL branch only
   * (e.g. `x-confirm-change-request`). The tRPC path ignores these —
   * `questionnaire_template_version` is a non-governed entity.
   */
  headers?: Record<string, string>;
}

/**
 * The tRPC `update` procedure returns `{ affected_rows }`, but the
 * GraphQL mutation returns the updated row's `Id`. Consumers only
 * `await` the call (they do not read the result), so we reshape to
 * the GraphQL shape using the `Id` that was submitted.
 */
const mapTrpcResponseToGraphQL = (
  id: string
): UpdateQuestionnaireTemplateVersionMutation => ({
  update_questionnaire_template_version_by_pk: {
    __typename: 'questionnaire_template_version',
    Id: id,
  },
});

/**
 * Flag-gated wrapper for the `updateQuestionnaireTemplateVersion`
 * mutation. Defaults to the legacy Apollo mutation and delegates to
 * the tRPC variant when the `trpc` feature flag is enabled.
 */
export const useUpdateQuestionnaireTemplateVersion = createMutationHook({
  name: 'updateQuestionnaireTemplateVersion',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.questionnaireTemplateVersion.update.mutationOptions({
      onSuccess,
    }),
  mapInput: (input: UpdateQuestionnaireTemplateVersionInput) => ({
    Id: input.Id,
    Version: input.Version,
    Status: input.Status,
    Schema: input.Schema,
    UISchema: input.UISchema,
    CustomAttributeData: input.CustomAttributeData ?? null,
  }),
  invalidate: (trpc) => [
    trpc.frontend.questionnaireTemplateVersion.pathKey(),
    trpc.frontend.questionnaireTemplate.pathKey(),
  ],
  mapResponse: (
    _result,
    input: UpdateQuestionnaireTemplateVersionInput
  ): UpdateQuestionnaireTemplateVersionMutation =>
    mapTrpcResponseToGraphQL(input.Id),
  graphqlDocument: UpdateQuestionnaireTemplateVersionDocument,
  graphqlVariables: (input: UpdateQuestionnaireTemplateVersionInput) => ({
    Id: input.Id,
    object: {
      Version: input.Version,
      Status: input.Status,
      Schema: input.Schema,
      UISchema: input.UISchema,
      CustomAttributeData: input.CustomAttributeData ?? null,
    },
  }),
  evictFields: [
    'questionnaire_template_version',
    'questionnaire_template',
    'questionnaire_template_by_pk',
  ],
  refetchQueries: [
    namedOperations.Query.getQuestionnaireTemplateById,
    namedOperations.Query.getQuestionnaireTemplates,
  ],
  apolloContext: (
    options: UpdateQuestionnaireTemplateVersionOptions | undefined
  ) => (options?.headers ? { headers: options.headers } : {}),
});
